import React, { useState, useRef } from 'react';
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Camera, X, Plus, Trash2, Check, ScanLine, AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { scanReceipt, getCroppedImg, type ScannedItem, type ScannedReceiptResult } from '../services/receiptScanner';
import { cn } from '../utils/cn';
import { formatCurrency } from '../utils/currencyFormatting';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../store/AuthContext';
import { LoadingSpinner } from './LoadingSpinner';
import * as Sentry from '@sentry/react';

interface ReceiptScannerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (result: ScannedReceiptResult) => void;
    currency: string;
}

type Step = 'capture' | 'crop' | 'processing' | 'review';

export const ReceiptScannerModal: React.FC<ReceiptScannerModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    currency,
}) => {
    const [step, setStep] = useState<Step>('capture');
    const [progress, setProgress] = useState(0);
    const [items, setItems] = useState<ScannedItem[]>([]);
    const [includeTax, setIncludeTax] = useState(false);
    const [scanResult, setScanResult] = useState<{ total: number; tax: number; serviceCharge: number; discounts: { name: string; amount: number }[] } | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [newName, setNewName] = useState('');
    const [newPrice, setNewPrice] = useState('');
    const [newCategory, setNewCategory] = useState<'Food' | 'Drink' | 'Other'>('Food');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    const [crop, setCrop] = useState<Crop>({ unit: '%', width: 100, height: 100, x: 0, y: 0 });
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const [isQuotaExceeded, setIsQuotaExceeded] = useState(false);
    const { user } = useAuth();

    React.useEffect(() => {
        if (!isOpen || !user) return;
        const today = new Date().toISOString().split('T')[0];
        const unsubscribe = onSnapshot(doc(db, 'userApiUsage', user.uid), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setIsQuotaExceeded(data.date === today && data.count >= 3);
            } else {
                setIsQuotaExceeded(false);
            }
        });
        return () => { unsubscribe(); };
    }, [isOpen, user]);

    if (!isOpen) return null;

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Show preview and go to crop step
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        setStep('crop');
        setCrop({ unit: '%', width: 100, height: 100, x: 0, y: 0 }); // Reset crop
        setCompletedCrop(undefined);
        setError(null);
    };

    const handleCropConfirm = async () => {
        if (!previewUrl || !imgRef.current) {
            // If no crop is set but we have an image, just use the whole image
            if (previewUrl && step === 'crop') {
                // Proceed without cropping (use original or full image)
                // If completedCrop is null, it typically means no selection, so full image.
            } else {
                return;
            }
        }

        setStep('processing');
        setProgress(0);
        setError(null);

        try {
            let blobToScan: Blob;

            if (completedCrop && imgRef.current) {
                // We need to scale the crop coordinates to the natural image size
                const image = imgRef.current;
                const scaleX = image.naturalWidth / image.width;
                const scaleY = image.naturalHeight / image.height;

                const pixelCrop = {
                    x: completedCrop.x * scaleX,
                    y: completedCrop.y * scaleY,
                    width: completedCrop.width * scaleX,
                    height: completedCrop.height * scaleY,
                };

                const croppedBlob = await getCroppedImg(previewUrl, pixelCrop);
                blobToScan = croppedBlob;

                // Update preview
                URL.revokeObjectURL(previewUrl);
                const croppedUrl = URL.createObjectURL(croppedBlob);
                setPreviewUrl(croppedUrl);
            } else {
                // Fallback: use original file (fetch blob from url)
                // Note: if user didn't crop, we scan the whole image
                const response = await fetch(previewUrl);
                blobToScan = await response.blob();
            }

            const file = new File([blobToScan], "receipt_processed.jpg", { type: "image/jpeg" });
            const parsed = await scanReceipt(file, setProgress);

            if (parsed.items.length === 0) {
                setError('No items could be detected. You can add items manually below.');
            }

            // Auto-detect if tax is already included in item prices (UK/EU) or added on top (US)
            const subtotal = parsed.items.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
            const isTaxAddedOnTop = Math.abs((subtotal + parsed.tax + parsed.serviceCharge) - parsed.total) < 0.5;
            setIncludeTax(isTaxAddedOnTop && parsed.tax > 0);

            setItems(parsed.items);
            setScanResult({
                total: parsed.total,
                tax: parsed.tax,
                serviceCharge: parsed.serviceCharge,
                discounts: parsed.discounts
            });
            setStep('review');
        } catch (err: unknown) {
            console.error(err);
            
            const isResourceExhausted = 
                (typeof err === 'object' && err !== null && 'code' in err && err.code === 'functions/resource-exhausted') ||
                (err instanceof Error && err.message.includes('resource-exhausted')) ||
                (typeof err === 'object' && err !== null && 'message' in err && typeof err.message === 'string' && err.message.includes('resource-exhausted'));

            if (isResourceExhausted) {
                setError('You have reached your daily limit of 3 receipt scans. Please try again tomorrow.');
                Sentry.captureMessage('Receipt scan limit exceeded', {
                    level: 'warning',
                    extra: { originalError: err }
                });
            } else {
                setError('Failed to process the receipt. Please try again with a clearer image.');
                Sentry.captureException(err);
            }
            setStep('capture');
        }
    };

    const handleLoadMockData = async (type: 'coffee' | 'dinner') => {
        setStep('processing');
        setProgress(30);

        // Simulate network delay
        await new Promise(r => setTimeout(r, 600));
        setProgress(100);

        const mockData = type === 'coffee'
            ? {
                items: [
                    { name: 'Latte (Large)', price: 4.50, quantity: 2, category: 'Drink' as const },
                    { name: 'Almond Croissant', price: 3.50, quantity: 1, category: 'Food' as const },
                    { name: 'Avocado Toast', price: 9.00, quantity: 1, category: 'Food' as const }
                ],
                total: 23.32,
                tax: 1.82,
                serviceCharge: 0,
                discounts: []
            }
            : {
                items: [
                    { name: 'Sparkling Water', price: 2.65, quantity: 6, category: 'Drink' as const },
                    { name: 'Ribeye Steak', price: 32.00, quantity: 1, category: 'Food' as const },
                    { name: 'Salmon Filet', price: 24.00, quantity: 1, category: 'Food' as const },
                    { name: 'Side Salad', price: 5.50, quantity: 2, category: 'Food' as const }
                ],
                total: 82.90,
                tax: 0,
                serviceCharge: 0,
                discounts: []
            };

        const parsedItems: ScannedItem[] = mockData.items.map(i => ({
            id: Math.random().toString(36).substring(2, 9),
            ...i,
            included: true,
        }));

        const subtotal = parsedItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
        const isTaxAddedOnTop = Math.abs((subtotal + mockData.tax + mockData.serviceCharge) - mockData.total) < 0.5;
        setIncludeTax(isTaxAddedOnTop && mockData.tax > 0);

        setItems(parsedItems);
        setScanResult({
            total: mockData.total,
            tax: mockData.tax,
            serviceCharge: mockData.serviceCharge,
            discounts: mockData.discounts
        });
        setStep('review');
    };

    const handleToggleItem = (id: string) => {
        setItems((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, included: !item.included } : item
            )
        );
    };

    const handleUpdateItem = (id: string, field: 'name' | 'price' | 'quantity' | 'category', value: string) => {
        setItems((prev) =>
            prev.map((item) => {
                if (item.id !== id) return item;
                if (field === 'name') return { ...item, name: value };
                if (field === 'category') return { ...item, category: value as 'Food' | 'Drink' | 'Other' };
                if (field === 'quantity') {
                    const quantity = parseInt(value, 10);
                    return { ...item, quantity: isNaN(quantity) || quantity < 1 ? 1 : quantity };
                }
                const price = parseFloat(value);
                return { ...item, price: isNaN(price) ? 0 : price };
            })
        );
    };

    const handleDeleteItem = (id: string) => {
        setItems((prev) => prev.filter((item) => item.id !== id));
    };

    const handleAddManualItem = (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!newName.trim() || !newPrice.trim()) return;
        const price = parseFloat(newPrice);
        if (isNaN(price) || price <= 0) return;

        setItems((prev) => [
            ...prev,
            {
                id: Math.random().toString(36).substring(2, 9),
                name: newName.trim(),
                price,
                quantity: 1,
                included: true,
                category: newCategory,
            },
        ]);
        setNewName('');
        setNewPrice('');
    };

    const handleConfirm = () => {
        const included = items.filter((item) => item.included);
        if (included.length === 0) return;
        onConfirm({
            items: included,
            total: scanResult?.total ?? 0,
            tax: includeTax ? (scanResult?.tax ?? 0) : 0,
            serviceCharge: scanResult?.serviceCharge ?? 0,
            discounts: scanResult?.discounts ?? []
        });
        handleReset();
    };

    const handleReset = () => {
        setStep('capture');
        setProgress(0);
        setItems([]);
        setIncludeTax(false);
        setScanResult(null);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
        setError(null);
        setNewName('');
        setNewPrice('');
        setNewCategory('Food');
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const includedItems = items.filter((i) => i.included);
    const subtotal = includedItems.reduce((sum, i) => sum + (i.price * (i.quantity || 1)), 0);
    // Add tax and service charge to the preview total if they exist
    const total = subtotal + (includeTax ? (scanResult?.tax ?? 0) : 0) + (scanResult?.serviceCharge ?? 0) - (scanResult?.discounts.reduce((sum, d) => sum + d.amount, 0) ?? 0);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

            {/* Modal */}
            <div className="relative flex flex-col w-full max-w-md max-h-[90vh] bg-bg-card rounded-2xl border border-border-default shadow-xl overflow-hidden z-10">
                {/* Header */}
                <div className="flex-none flex items-center justify-between px-4 py-3 border-b border-border-default">
                    <div className="flex items-center gap-2">
                        <ScanLine size={20} className="text-primary-500" />
                        <h3 className="text-lg font-bold text-text-heading">Scan Receipt</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        {step === 'review' && (
                            <Button variant="ghost" size="sm" onClick={handleReset}>
                                Rescan
                            </Button>
                        )}
                        <button onClick={handleClose} className="rounded-full p-2 hover:bg-bg-hover transition-colors" aria-label="Close scanner">
                            <X size={20} className="text-text-faint" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    {/* ───── STEP 1: CAPTURE ───── */}
                    {step === 'capture' && (
                        <div className="flex flex-col items-center justify-center p-8 gap-6 min-h-[400px]">
                            <div className="rounded-full bg-primary-500/10 p-6">
                                <Camera size={48} className="text-primary-500" />
                            </div>
                            <div className="text-center space-y-2">
                                <h4 className="text-lg font-bold text-text-heading">Take a photo of your receipt</h4>
                                <p className="text-sm text-text-muted max-w-[280px]">
                                    Make sure the receipt is well-lit and all items are visible
                                </p>
                            </div>

                            {isQuotaExceeded && (
                                <div className="flex items-start gap-2 rounded-xl bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-600 dark:text-red-400">
                                    <AlertTriangle size={16} className="mt-0.5 flex-none" />
                                    You have reached your daily limit of 3 receipt scans. Please try again tomorrow.
                                </div>
                            )}

                            {error && !isQuotaExceeded && (
                                <div className="flex items-start gap-2 rounded-xl bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-600 dark:text-red-400">
                                    <AlertTriangle size={16} className="mt-0.5 flex-none" />
                                    {error}
                                </div>
                            )}

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={handleFileSelect}
                                className="hidden"
                                disabled={isQuotaExceeded}
                            />
                            <div className="flex flex-col gap-3 w-full max-w-[240px]">
                                <Button
                                    className="w-full h-12"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isQuotaExceeded}
                                >
                                    <Camera size={18} className="mr-2" />
                                    Open Camera
                                </Button>
                                <Button
                                    variant="outline"
                                    className="w-full"
                                    disabled={isQuotaExceeded}
                                    onClick={() => {
                                        // Create a separate input without capture for gallery
                                        const input = document.createElement('input');
                                        input.type = 'file';
                                        input.accept = 'image/*';
                                        input.onchange = (e) =>
                                            { handleFileSelect(e as unknown as React.ChangeEvent<HTMLInputElement>); };
                                        input.click();
                                    }}
                                >
                                    Choose from Gallery
                                </Button>
                            </div>

                            {import.meta.env.DEV && (
                                <div className="mt-2 pt-4 border-t border-border-default/50 w-full max-w-[240px] flex flex-col gap-2">
                                    <p className="text-[10px] uppercase font-bold text-text-faint text-center tracking-widest">Dev Mode: Mock Data</p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full text-primary-600 border-primary-200 bg-primary-50 hover:bg-primary-100"
                                        onClick={() => handleLoadMockData('coffee')}
                                    >
                                        Load Coffee Shop Mock
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full text-primary-600 border-primary-200 bg-primary-50 hover:bg-primary-100"
                                        onClick={() => handleLoadMockData('dinner')}
                                    >
                                        Load Dinner Mock
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ───── STEP 2: CROP ───── */}
                    {step === 'crop' && previewUrl && (
                        <div className="flex flex-col flex-1 min-h-0">
                            <div className="relative flex-1 bg-black min-h-[250px] flex items-center justify-center p-4 overflow-auto">
                                <ReactCrop
                                    crop={crop}
                                    onChange={(c) => { setCrop(c); }}
                                    onComplete={(c) => { setCompletedCrop(c); }}
                                >
                                    <img
                                        ref={imgRef}
                                        src={previewUrl}
                                        alt="Crop receipt"
                                        className="max-h-[55vh] w-auto object-contain"
                                    />
                                </ReactCrop>
                            </div>

                            <div className="flex-none p-4 space-y-4 bg-bg-card border-t border-border-default">
                                <p className="text-sm text-text-muted text-center">
                                    Drag to crop the receipt, or skip to scan the whole image.
                                </p>
                                <div className="flex gap-3">
                                    <Button variant="outline" className="flex-1" onClick={handleReset}>
                                        Cancel
                                    </Button>
                                    <Button className="flex-1" onClick={handleCropConfirm}>
                                        Scan Receipt <ArrowRight size={16} className="ml-2" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ───── STEP 3: PROCESSING ───── */}
                    {step === 'processing' && (
                        <div className="flex flex-col items-center justify-center p-8 gap-6 min-h-[400px]">
                            {previewUrl && (
                                <div className="w-48 h-64 rounded-xl overflow-hidden border border-border-default shadow-lg">
                                    <img src={previewUrl} alt="Receipt" className="w-full h-full object-cover" />
                                </div>
                            )}
                            <div className="text-center space-y-3">
                                <LoadingSpinner size="lg" className="mx-auto" />
                                <h4 className="text-lg font-bold text-text-heading">Reading your receipt…</h4>
                                <p className="text-sm text-text-muted">This may take a few seconds</p>
                            </div>
                            {/* Progress bar */}
                            <div className="w-full max-w-[240px]">
                                <div className="h-2 rounded-full bg-bg-card-alt overflow-hidden">
                                    <div
                                        className="h-full rounded-full bg-primary-500 transition-all duration-300"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                                <p className="text-xs text-text-faint text-center mt-1">{progress}%</p>
                            </div>
                        </div>
                    )}

                    {/* ───── STEP 4: REVIEW & EDIT ───── */}
                    {step === 'review' && (
                        <div className="p-4 space-y-4">
                            {error && (
                                <div className="flex items-start gap-2 rounded-xl bg-amber-500/10 border border-amber-500/20 p-3 text-sm text-amber-600 dark:text-amber-400">
                                    <AlertTriangle size={16} className="mt-0.5 flex-none" />
                                    {error}
                                </div>
                            )}

                            {/* Items List */}
                            {items.length > 0 && (
                                <div className="space-y-2">
                                    <h4 className="text-[10px] font-black uppercase tracking-widest text-text-faint">
                                        Detected Items ({includedItems.length}/{items.length})
                                    </h4>
                                    {items.map((item) => (
                                        <div
                                            key={item.id}
                                            className={cn(
                                                "flex items-center gap-2 rounded-xl border p-3 transition-all",
                                                item.included
                                                    ? "border-border-default bg-bg-card"
                                                    : "border-border-default/50 bg-bg-card-alt opacity-50"
                                            )}
                                        >
                                            <button
                                                onClick={() => { handleToggleItem(item.id); }}
                                                className={cn(
                                                    "flex-none flex h-6 w-6 items-center justify-center rounded-full transition-colors",
                                                    item.included
                                                        ? "bg-primary-500 text-white"
                                                        : "border-2 border-border-default"
                                                )}
                                            >
                                                {item.included && <Check size={14} />}
                                            </button>

                                            <div className="flex-1 min-w-0 space-y-2">
                                                <input
                                                    value={item.name}
                                                    onChange={(e) => { handleUpdateItem(item.id, 'name', e.target.value); }}
                                                    className="w-full bg-transparent text-sm font-medium text-text-heading outline-none placeholder:text-text-faint"
                                                    placeholder="Item name"
                                                />
                                                <div className="flex items-center gap-3">
                                                    <div className="flex items-center gap-1">
                                                        <span className="text-xs text-text-faint">Qty</span>
                                                        <input
                                                            value={item.quantity}
                                                            onChange={(e) => { handleUpdateItem(item.id, 'quantity', e.target.value); }}
                                                            type="number"
                                                            min="1"
                                                            className="w-12 bg-transparent text-sm font-bold text-text-heading text-center outline-none tabular-nums border border-border-default/50 rounded bg-bg-card-alt px-1 py-0.5"
                                                        />
                                                    </div>

                                                    <div className="flex items-center gap-1">
                                                        <span className="text-xs text-text-faint">{formatCurrency(0, currency).replace(/\d/g, '').replace(/[.,]/g, '').trim()}</span>
                                                        <input
                                                            value={item.price}
                                                            onChange={(e) => { handleUpdateItem(item.id, 'price', e.target.value); }}
                                                            type="number"
                                                            step="0.01"
                                                            className="w-16 bg-transparent text-sm font-bold text-text-heading outline-none tabular-nums"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex gap-1">
                                                    {(['Food', 'Drink', 'Other'] as const).map((cat) => (
                                                        <button
                                                            key={cat}
                                                            type="button"
                                                            onClick={() => { handleUpdateItem(item.id, 'category', cat); }}
                                                            className={cn(
                                                                'px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors',
                                                                item.category === cat
                                                                    ? 'bg-primary-500 text-white'
                                                                    : 'bg-bg-card-alt text-text-muted hover:bg-bg-base'
                                                            )}
                                                        >
                                                            {cat}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => { handleDeleteItem(item.id); }}
                                                className="flex-none p-1 text-text-faint hover:text-red-500 transition-colors self-start mt-0.5"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Add Manual Item */}
                            <form onSubmit={handleAddManualItem} className="space-y-2 border-t border-border-default/50 pt-3">
                                <div className="flex items-end gap-2">
                                    <div className="flex-1">
                                        <Input
                                            label="Add item"
                                            placeholder="Item name"
                                            value={newName}
                                            onChange={(e) => { setNewName(e.target.value); }}
                                        />
                                    </div>
                                    <div className="w-20">
                                        <Input
                                            label="Price"
                                            placeholder="0.00"
                                            type="number"
                                            step="0.01"
                                            value={newPrice}
                                            onChange={(e) => { setNewPrice(e.target.value); }}
                                        />
                                    </div>
                                    <Button type="submit" size="icon" variant="ghost" className="mb-0.5">
                                        <Plus size={18} />
                                    </Button>
                                </div>
                                <div className="flex gap-1">
                                    {(['Food', 'Drink', 'Other'] as const).map((cat) => (
                                        <button
                                            key={cat}
                                            type="button"
                                            onClick={() => { setNewCategory(cat); }}
                                            className={cn(
                                                'px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors',
                                                newCategory === cat
                                                    ? 'bg-primary-500 text-white'
                                                    : 'bg-bg-card-alt text-text-muted hover:bg-bg-base'
                                            )}
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                </div>
                            </form>

                            {/* Native Bill Extras Summary */}
                            {scanResult && (scanResult.tax > 0 || scanResult.serviceCharge > 0 || (scanResult.discounts.length > 0)) && (
                                <div className="mt-6 space-y-2 rounded-xl bg-bg-card-alt p-4 border border-border-default">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-text-faint mb-3">
                                        Additional Bill Items (Applied to Tab)
                                    </h4>

                                    {scanResult.tax > 0 && (
                                        <label className="flex justify-between items-center text-sm cursor-pointer group">
                                            <div className="flex items-center gap-2 text-text-muted group-hover:text-text-heading transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={includeTax}
                                                    onChange={(e) => { setIncludeTax(e.target.checked); }}
                                                    className="rounded border-border-default text-primary-500 focus:ring-primary-500"
                                                />
                                                Tax (Add as line item)
                                            </div>
                                            <span className="font-semibold text-text-heading">{formatCurrency(scanResult.tax, currency)}</span>
                                        </label>
                                    )}

                                    {scanResult.serviceCharge > 0 && (
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-text-muted">Service Charge</span>
                                            <span className="font-semibold text-text-heading">{formatCurrency(scanResult.serviceCharge, currency)}</span>
                                        </div>
                                    )}

                                    {scanResult.discounts.length > 0 && scanResult.discounts.map((discount, i) => (
                                        <div key={i} className="flex justify-between items-center text-sm">
                                            <span className="text-text-muted">{discount.name}</span>
                                            <span className="font-semibold text-emerald-500">-{formatCurrency(discount.amount, currency)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer (review step only) */}
                {step === 'review' && (
                    <div className="flex-none border-t border-border-default p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-text-muted">
                                {includedItems.length} item{includedItems.length !== 1 ? 's' : ''} selected
                            </span>
                            <span className="text-lg font-black text-text-heading">
                                {formatCurrency(total, currency)}
                            </span>
                        </div>
                        <Button
                            className="w-full h-12"
                            onClick={handleConfirm}
                            disabled={includedItems.length === 0}
                        >
                            <Check size={18} className="mr-2" />
                            Add {includedItems.length} Item{includedItems.length !== 1 ? 's' : ''} to Bill
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};
